import styled, { css, keyframes } from 'styled-components';
import { ExamsScope } from '../shared/theme';
import { exFont } from '../shared/typography';

// Wizard shell styles (tplA 413–448 header + stepper, 582/731 footer).

export const Shell = styled(ExamsScope)`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: var(--ex-surface-2);
`;

// ------------------------------------------------------------------ header
export const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 66px;
  padding: 0 26px;
  background: var(--ex-surface);
  border-bottom: 1px solid var(--ex-line);
  flex: 0 0 auto;
`;

export const CloseButton = styled.button`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 -5px;
  padding: 0;
  border: none;
  border-radius: var(--ex-radius-pill);
  background: transparent;
  color: var(--ex-ink);
  cursor: pointer;
  &:hover {
    background: var(--ex-surface-2);
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const TitleBlock = styled.div`
  min-width: 0;
  flex: 0 1 240px;
`;

export const Kicker = styled.p`
  ${exFont(600, 11)}
  margin: 0;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ex-purple);
`;

export const Title = styled.p`
  ${exFont(600, 15.5)}
  margin: 2px 0 0;
  color: var(--ex-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ------------------------------------------------------------ question peek
export const PeekWrap = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

export const PeekButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: var(--ex-radius-pill);
  border: 1px solid var(--ex-line);
  background: ${({ $open }) =>
    $open ? 'var(--ex-purple-100)' : 'var(--ex-surface)'};
  color: var(--ex-purple);
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const PeekScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
`;

export const PeekPanel = styled.div`
  position: absolute;
  top: 46px;
  left: 0;
  z-index: 70;
  width: 420px;
  max-width: 80vw;
  max-height: 70vh;
  overflow-y: auto;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  box-shadow: var(--ex-shadow-3);
  padding: 16px 18px;
`;

export const PeekHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

export const PeekHeading = styled.span`
  ${exFont(600, 'xs')}
  flex: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ex-ink-2);
`;

export const PeekMarks = styled.span`
  ${exFont(600, 's')}
  color: var(--ex-ink);
  white-space: nowrap;
`;

export const PeekList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const PeekItem = styled.li`
  margin: 0;
  padding-left: ${({ $depth }) => ($depth > 0 ? '16px' : '0')};
`;

export const PeekItemHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

export const PeekLabel = styled.span`
  ${exFont(600, 's')}
  flex: 1;
  color: var(--ex-ink);
`;

export const PeekText = styled.p`
  ${exFont(400, 'm')}
  margin: 4px 0 0;
  line-height: 1.55;
  color: var(--ex-ink);
  white-space: pre-wrap;
`;

export const PeekNote = styled.p`
  ${exFont(500, 's')}
  margin: 6px 0 0;
  color: var(--ex-ink-strong);
`;

export const PeekEdit = styled.button`
  ${exFont(600, 's')}
  margin-top: 12px;
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  cursor: pointer;
`;

// ------------------------------------------------------------------ stepper
export const StepperScroll = styled.nav`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  display: flex;
  scrollbar-width: none;
`;

export const StepperRow = styled.ol`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin: 0 0 0 auto;
  padding: 4px 0;
  list-style: none;
`;

export const StepItem = styled.li`
  display: flex;
  align-items: center;
`;

export const StepConnector = styled.span`
  width: 12px;
  height: 1px;
  margin: 0 6px;
  background: var(--ex-line-strong);
  @media (min-width: 1240px) {
    width: 18px;
  }
`;

const STEP_TONES = {
  viewing: css`
    background: var(--ex-purple);
    color: var(--white);
    border-color: var(--ex-purple);
  `,
  done: css`
    background: var(--ex-surface);
    color: var(--ex-purple);
    border-color: var(--ex-purple);
  `,
  open: css`
    background: var(--ex-surface);
    color: var(--ex-ink-2);
    border-color: var(--ex-ink-faint);
  `,
};

export const StepButton = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: none;
  background: none;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 3px;
    border-radius: var(--ex-radius-item);
  }
`;

export const StepNumber = styled.span`
  ${exFont(600, 11.5)}
  width: 24px;
  height: 24px;
  border-radius: var(--ex-radius-pill);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid;
  box-sizing: border-box;
  ${({ $tone }) => STEP_TONES[$tone] || STEP_TONES.open}
`;

export const StepLabel = styled.span`
  ${({ $viewing }) => exFont($viewing ? 600 : 500, 's')}
  white-space: nowrap;
  color: ${({ $tone }) =>
    $tone === 'open' ? 'var(--ex-ink-2)' : 'var(--ex-purple)'};
  ${({ $viewing }) =>
    !$viewing &&
    css`
      @media (max-width: 1239px) {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
    `}
`;

// ------------------------------------------------------------------ banners
export const Banner = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 26px;
  background: var(--ex-warn-bg);
  color: var(--ex-warn-ink);
  border-bottom: 1px solid var(--ex-line);
`;

export const BannerText = styled.span`
  flex: 1;
`;

export const BannerAction = styled.button`
  ${exFont(600, 's')}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  cursor: pointer;
`;

// --------------------------------------------------------------------- body
export const Body = styled.main`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const StepErrors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 28px 0;
`;

// ------------------------------------------------------------------- footer
export const Footer = styled.footer`
  position: sticky;
  bottom: 0;
  z-index: 20;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 28px;
  border-top: 1px solid var(--ex-line);
  background: var(--ex-surface);
`;

export const BackLink = styled.button`
  ${exFont(600, 'l')}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const FooterSpacer = styled.span`
  flex: 1;
`;

// A neutral footer line (not validation): e.g. credits the free exam covers.
export const FooterNote = styled.span`
  ${exFont(500, 's')}
  color: var(--ex-ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const Summary = styled.span`
  ${exFont(500, 's')}
  color: var(--ex-bad-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const FooterActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  [data-testid='exam-footer-primary'] {
    ${exFont(600, 15)}
    padding: 13px 28px;
    border: none;
    &:disabled {
      background: var(--ex-disabled-bg);
      opacity: 1;
    }
  }
`;

export const SaveStatusRoot = styled.span`
  ${exFont(500, 12.5)}
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  margin-right: 24px;
  white-space: nowrap;
  color: ${({ $error }) => ($error ? 'var(--ex-bad-ink)' : 'var(--ex-ink-2)')};
`;

export const RetryLink = styled.button`
  ${exFont(600, 'xs')}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  cursor: pointer;
`;

// ---------------------------------------------------------- loading / error
export const MessageRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 24px;
`;

export const MessageCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  max-width: 420px;
`;

export const MessageTitle = styled.p`
  ${exFont(600, 'l')}
  margin: 0;
  color: var(--ex-ink);
`;

export const MessageBody = styled.p`
  ${exFont(400, 'm')}
  margin: 0;
  color: var(--ex-ink-2);
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid var(--ex-purple-150);
  border-top-color: var(--ex-purple);
  animation: ${spin} 0.9s linear infinite;
`;
